import { Dialog, DialogContent, DialogTitle, Button, Stack, List, ListItem, ListItemText, IconButton, TextField, CircularProgress, Tooltip, MenuItem, DialogContentText } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import Select from '@mui/material/Select';
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from "react-redux";
import { ITimecard } from "../../interface/ISelectedDate";
import { setSelectedDate } from "../../source/slice/SelectedDateSlice";
import { applyTemplateToJournal, applyTemplateToTimecard, buildJournalTemplate, buildTemplate, fetchTemplates, getTemplateHeader, postTemplate, deleteTemplate, setDefaultTemplate } from "../../utils/functions/templateUtils";
import { useMsal } from '@azure/msal-react'
import { getAuthConfig } from '../../utils/authApi'
import { setTemplates } from '../../source/slice/TemplateSlice'
import { IJournal } from '@/interface/IJournal';
import { setSelectedJournal } from '@/source/slice/JournalSlice';
import { IUser } from '@/interface/IUser';
import { IProject } from '@/interface/IProjects';

interface TemplateProps {
  onClose: () => void,
  isDayOpen: boolean
}

const Template: React.FC<TemplateProps> = ({ onClose, isDayOpen }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation()
  const selectedDate: ITimecard = useSelector((s: any) => s.selectedDate?.value);
  const selectedJournal: IJournal = useSelector((s: any) => s.journal?.value);
  const user: IUser = useSelector((s: any) => s.user?.value);
  const projects: IProject[] = useSelector((s: any) => s.projects?.value || []);
  const templates = useSelector((s: any) => s.template?.value || []);
  const [loading, setLoading] = useState<boolean>(true);
  const { instance, accounts } = useMsal();
  const [tempName, setTempName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const isJournalUser = user?.worker?.mserp_usetimecard === false;

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTempName(value);
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (mounted) setLoading(true);
        const data = await fetchTemplates(false);
        if (mounted) {
          dispatch(setTemplates(data));
          setSelectedTemplateId(getTemplateHeader(data.find((tpl: any) => getTemplateHeader(tpl)?.default))?.timecardheadertemplatesid || "");
        }
      } catch (error) {
        console.log('Failed to load templates', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (instance && accounts && accounts[0]) load();
    else {
      // no auth available -> not loading
      if (mounted) setLoading(false);
    }
    return () => { mounted = false };
  }, [instance, accounts, dispatch]);

  const handleSaveTemplate = async () => {
    let mounted = true;
    try {
      if (mounted) setLoading(true);
      const tpl = isJournalUser ? buildJournalTemplate(selectedJournal, tempName, projects, user) : buildTemplate(selectedDate, tempName);
      if (!tpl) {
        if (mounted) setLoading(false);
        return;
      }
      const config = await getAuthConfig(instance, accounts[0]);
      await postTemplate(tpl, config);
      const refreshed = await fetchTemplates(true);
      if (mounted) {
        dispatch(setTemplates(refreshed));
        setTempName("");
      }
    } catch (error) {
      console.log('Failed to save template', error);
    } finally {
      if (mounted) setLoading(false);
      mounted = false;
    }
  };

  const handleDeleteTemplate = async (id?: string) => {
    if (!id) return;
    let mounted = true;
    try {
      if (mounted) setLoading(true);
      const config = await getAuthConfig(instance, accounts[0]);
      await deleteTemplate(id, config);
      const refreshed = await fetchTemplates(true);
      if (mounted) dispatch(setTemplates(refreshed));
    } catch (error) {
      console.log('Failed to delete template', error);
    } finally {
      if (mounted) setLoading(false);
      mounted = false;
    }
  };

  const handleTemplateSelect = async (id: string) => {
    let mounted = true;
    try {
      if (mounted) setLoading(true);
      const config = await getAuthConfig(instance, accounts[0]);
      if (mounted && id) {
        await setDefaultTemplate(id, config);
        const refreshed = await fetchTemplates(true);
        if (mounted) {
          dispatch(setTemplates(refreshed));
          setSelectedTemplateId(getTemplateHeader(refreshed.find((template: any) => getTemplateHeader(template)?.default))?.timecardheadertemplatesid || "");
        }
      }
    } catch (error) {
      console.log('Failed to select template', error);
    } finally {
      if (mounted) setLoading(false);
      mounted = false;
    }
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('template.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label={t('template.templateNameLabel')} variant="outlined" fullWidth onChange={handleTextFieldChange} />
          <Button variant="contained" onClick={handleSaveTemplate}>{t('template.saveCurrentDay')}</Button>
          {loading && (
            <Stack sx={{ alignItems: 'center' }}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {!loading && Array.isArray(templates) && templates.length === 0 && <ListItemText primary={t('template.noTemplates')} />}
          {!loading && Array.isArray(templates) && templates.length > 0 && (
            <List dense>
              {templates.map((template: any) => {
                const header = getTemplateHeader(template) || {};
                const id = header?.timecardheadertemplatesid;
                const name = header?.templatename;
                return (
                  <ListItem key={id} secondaryAction={<>
                    <Tooltip title={isDayOpen ? t('template.insert') : t('template.tooltip_disabled')}>
                      <span style={{ display: 'inline-block' }}>
                        <IconButton onClick={() => {
                          if (isJournalUser) {
                            const applied = applyTemplateToJournal(selectedJournal, template, user);
                            if (applied) dispatch(setSelectedJournal(applied));
                          } else {
                            const applied = applyTemplateToTimecard(selectedDate, template);
                            if (applied) dispatch(setSelectedDate(applied));
                          }
                          onClose();
                        }} size="small"
                          disabled={!isDayOpen}>
                          <ContentPasteGoIcon fontSize="small" color={isDayOpen ? "primary" : "disabled"} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>}>
                    <Tooltip title={t('template.delete')}>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteTemplate(id)}
                        size="small"
                        sx={{ mr: 2 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <ListItemText primary={name} />
                  </ListItem>
                )
              })}
            </List>
          )}
          <DialogContentText>{t('template.defaultTemplate')}</DialogContentText>
          <Select
            displayEmpty
            fullWidth
            value={selectedTemplateId}
            onChange={async (e) => {
              const id = e.target.value as string;
              setSelectedTemplateId(id);
              if (id) await handleTemplateSelect(id)
              else handleTemplateSelect("none")
            }}
            renderValue={(selected) => {
              if (!selected) return <em>{t('template.selectTemplate')}</em>;
              const tpl = (templates || []).find((x: any) => {
                const header = getTemplateHeader(x) || {};
                const id = header?.timecardheadertemplatesid;
                return id === selected;
              });
              if (!tpl) return String(selected);
              const header = getTemplateHeader(tpl) || {};
              const name = header?.templatename;
              return name || String(selected);
            }}
          >
            <MenuItem value="">
              <em>{t('template.selectTemplate')}</em>
            </MenuItem>
            {(Array.isArray(templates) ? templates : []).map((template: any) => {
              const header = getTemplateHeader(template) || {};
              const id = header?.timecardheadertemplatesid;
              const name = header?.templatename;
              return (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              )
            })}
          </Select>
          <Button onClick={onClose}>{t('template.close')}</Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default Template;
